import { Button, ButtonProps, Divider, Paper, styled, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import { useRecordOpenMode } from 'src/graphql/entities/record-open-mode/record-open-mode';
import { RecordOpenMode } from 'src/graphql/entities/user-setting-value/user-setting-value.types';
import { useLongPress } from 'use-long-press';

export const TriggerButton = styled(Button)(({ theme }) => ({
  minWidth: '7.5rem',
  width: '7.5rem',
  color: theme.palette.custom.OV.sys.onSurface,
  border: `1px solid ${theme.palette.custom.OV.sys.outline}`,

  '&:hover': {
    border: `1px solid ${theme.palette.custom.OV.sys.outline}`,
  },
}));

export const InnerButton = styled(Button)<{ selected?: boolean }>(({ theme, selected }) => ({
  width: '10rem',
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  color: theme.palette.custom.OV.sys.onSurface,
  textTransform: 'none',
  border: `1px solid ${selected ? theme.palette.custom.OV.sys.outline : 'transparent'}`,
}));

type EntityOpenModeSwitcherProps = {
  openMode: RecordOpenMode;
  onClick: () => void;
} & ButtonProps;

export const EntityOpenModeSwitcher = ({
  openMode,
  onClick,
  ...rest
}: EntityOpenModeSwitcherProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null);

  const { currentOpenMode, changeRecordOpenMode } = useRecordOpenMode();

  const [tooltipOpen, setTooltipOpen] = useState(false);

  const handlers = useLongPress(() => {
    setTooltipOpen(true);
  });

  const changeMode = (newMode: RecordOpenMode) => {
    changeRecordOpenMode(newMode);
    setTooltipOpen(false);
  };

  return (
    <Tooltip
      nonInteractable={false}
      open={tooltipOpen}
      onOpen={() => setTooltipOpen(true)}
      onClose={() => setTooltipOpen(false)}
      title={
        <Paper
          sx={{
            p: 2,
            maxWidth: 200,
            backgroundImage:
              'linear-gradient(rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.12))',
          }}
        >
          <Typography variant="caption" color="text.secondary" fontSize={12}>
            Set as default behavior:
          </Typography>
          <InnerButton
            variant="text"
            selected={currentOpenMode === RecordOpenMode.SidePeek}
            onClick={() => changeMode(RecordOpenMode.SidePeek)}
            size="small"
            sx={{ mb: 1, mt: 1 }}
            startIcon={
              <DynamicIcon
                name="side_navigation"
                sx={{
                  transform: 'scaleX(-1) scale(1.15, 0.95)',
                  position: 'relative',
                  right: '3px',
                }}
              />
            }
          >
            Always Side Peek
          </InnerButton>
          <InnerButton
            variant="text"
            size="small"
            selected={currentOpenMode === RecordOpenMode.FullPage}
            onClick={() => changeMode(RecordOpenMode.FullPage)}
            startIcon={
              <DynamicIcon
                name="aspect_ratio"
                sx={{
                  transform: 'scaleX(-1)',
                  position: 'relative',
                  left: '-3px',
                  marginBottom: '1px',
                }}
              />
            }
          >
            Always Full Page
          </InnerButton>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary" fontSize={12}>
            Tip: Click button directly for quick toggle
          </Typography>
        </Paper>
      }
      placement="bottom"
      enterDelay={1000}
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            p: 0,
            m: 0,
          },
        },
      }}
    >
      <TriggerButton
        ref={buttonRef}
        variant="outlined"
        onClick={onClick}
        {...handlers()}
        size="small"
        startIcon={
          openMode === RecordOpenMode.SidePeek ? (
            <DynamicIcon
              name="side_navigation"
              sx={{
                transform: 'scaleX(-1) scale(1.15, 0.95)',
              }}
            />
          ) : (
            <DynamicIcon
              name="aspect_ratio"
              sx={{
                transform: 'scaleX(-1)',
              }}
            />
          )
        }
        {...rest}
      >
        {openMode === RecordOpenMode.SidePeek ? 'Side Peek' : 'Full Page'}
      </TriggerButton>
    </Tooltip>
  );
};
